import { __awaiter, __decorate, __param } from "tslib";
import { FORCE_HTTP } from '@genesislcap/foundation-utils';
import { observable, volatile } from '@microsoft/fast-element';
import { DI, optional } from '@microsoft/fast-foundation';
import { MetaCache } from '../metadata';
import { isWebSocketExpression, logger } from '../utils';
import { ConnectConfig } from './connect.types';
import { HttpConnect } from './http.connect';
import { EventMessageType, MessageBuilder } from './message';
import { Socket } from './socket';
/**
 * The default configuration for connecting to a WS service.
 * @public
 */
export const defaultConnectConfig = {
    connect: {
        alwaysOn: false,
        enableHeartbeat: true,
        enableHeartbeatLogging: false,
        heartbeatInterval: 10000,
        enableMessageLogging: false,
    },
    reconnect: {
        reconnectAttempts: 10,
        reconnectInterval: 10000,
        reconnectStreams: true,
    },
};
/**
 * The default implementation for the Connect interface (WS-only).
 * @public
 */
let DefaultConnect = class DefaultConnect {
    get isConnected() {
        return this.socket.isConnected;
    }
    get isConnectedSubject() {
        return this.socket.isConnectedSubject;
    }
    get isConnected$() {
        var _a;
        return (_a = this.socket.isConnectedSubject) === null || _a === void 0 ? void 0 : _a.asObservable();
    }
    constructor(socket, messageBuilder, metaCache, config = defaultConnectConfig) {
        this.socket = socket;
        this.messageBuilder = messageBuilder;
        this.metaCache = metaCache;
        this.config = config;
        this.isWorking = false;
    }
    httpMode() {
        return false;
    }
    setValidSession(valid) {
        this.socket.hasValidSession = valid;
    }
    send(message, needsHandling) {
        return this.socket.send(message, needsHandling);
    }
    connect(host) {
        return __awaiter(this, void 0, void 0, function* () {
            this.socket.reset();
            if (!isWebSocketExpression.test(host)) {
                logger.error(`Specified host (${host}) is not valid WebSocket url. Connection will not proceed.`);
                logger.warn(`Make sure the host url starts with wss:// or ws://`);
                return;
            }
            return this.socket.connect(host, this.config.connect, this.config.reconnect);
        });
    }
    disconnect() {
        this.socket.reset();
    }
    snapshot(resourceName, params) {
        return __awaiter(this, void 0, void 0, function* () {
            const snapshotMessage = this.messageBuilder.createDataLogonMessage(resourceName, params);
            logger.debug(`${snapshotMessage.MESSAGE_TYPE} (snapshot) message created -> `, snapshotMessage);
            return this.socket.send(snapshotMessage);
        });
    }
    stream(resourceName, onMessage, onError, params) {
        const streamMessage = this.messageBuilder.createDataLogonMessage(resourceName, params);
        logger.debug(`${streamMessage.MESSAGE_TYPE} (stream) message created -> `, streamMessage);
        return this.socket.sendForStream(streamMessage, onMessage, onError);
    }
    getMoreRows(sourceRef) {
        return __awaiter(this, void 0, void 0, function* () {
            const moreRowsMessage = this.messageBuilder.createMoreRowsMessage(sourceRef);
            logger.debug(`${moreRowsMessage.MESSAGE_TYPE} message created -> `, moreRowsMessage);
            return this.socket.send(moreRowsMessage, false);
        });
    }
    dataLogoff(streamSourceRef) {
        return __awaiter(this, void 0, void 0, function* () {
            const dataLogoffMessage = this.messageBuilder.createDataLogoffMessage(streamSourceRef);
            logger.debug(`${dataLogoffMessage.MESSAGE_TYPE} message created -> `, dataLogoffMessage);
            return this.socket.send(dataLogoffMessage);
        });
    }
    getMetadata(resourceName, useCache = true) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (useCache) {
                if (this.metaCache.hasMetadataFor(resourceName)) {
                    logger.debug(`Cached metadata retrieved for ${resourceName}`);
                    return this.metaCache.getMetadataFor(resourceName);
                }
            }
            const metaRequestMessage = this.messageBuilder.createMetaRequestMessage(resourceName);
            logger.debug(`${metaRequestMessage.MESSAGE_TYPE} (metadata) message created -> `, metaRequestMessage);
            let details;
            try {
                this.isWorking = true;
                const metaResponse = yield this.socket.send(metaRequestMessage);
                details = (_a = metaResponse === null || metaResponse === void 0 ? void 0 : metaResponse.DETAILS) !== null && _a !== void 0 ? _a : {};
                if (Object.keys(details).length) {
                    this.metaCache.setMetadataFor(resourceName, details);
                }
            }
            catch (e) {
                logger.error(e);
            }
            finally {
                this.isWorking = false;
            }
            return details;
        });
    }
    getJSONSchema(resourceName, useCache = true) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (useCache) {
                if (this.metaCache.hasJsonSchemaFor(resourceName)) {
                    logger.debug(`Cached JsonSchema retrieved for ${resourceName}`);
                    return this.metaCache.getJsonSchemaFor(resourceName);
                }
            }
            const metaRequestMessage = this.messageBuilder.createMetaRequestMessage(resourceName, EventMessageType.JSON_SCHEMA_REQUEST);
            logger.debug(`${metaRequestMessage.MESSAGE_TYPE} (json schema) message created -> `, metaRequestMessage);
            let jsonSchema;
            try {
                this.isWorking = true;
                jsonSchema = (_a = (yield this.socket.send(metaRequestMessage))) !== null && _a !== void 0 ? _a : {};
                if (Object.keys(jsonSchema).length) {
                    this.metaCache.setJsonSchemaFor(resourceName, jsonSchema);
                }
            }
            catch (e) {
                logger.error(e);
            }
            finally {
                this.isWorking = false;
            }
            return jsonSchema;
        });
    }
    request(resourceName, params) {
        const requestMessage = this.messageBuilder.createRequestMessage(resourceName, params);
        logger.debug(`${requestMessage.MESSAGE_TYPE} (request server) message created -> `, requestMessage);
        return this.socket.send(requestMessage);
    }
    commitEvent(eventName, params) {
        return __awaiter(this, void 0, void 0, function* () {
            const commitMessage = this.messageBuilder.createCommitMessage(eventName, params);
            logger.debug(`${commitMessage.MESSAGE_TYPE} (commit) message created -> `, commitMessage);
            return this.socket.send(commitMessage);
        });
    }
};
__decorate([
    observable
], DefaultConnect.prototype, "isWorking", void 0);
__decorate([
    volatile
], DefaultConnect.prototype, "isConnected", null);
DefaultConnect = __decorate([
    __param(0, Socket),
    __param(1, MessageBuilder),
    __param(2, MetaCache),
    __param(3, optional(ConnectConfig))
], DefaultConnect);
export { DefaultConnect };
const useHttpConnect = FORCE_HTTP === 'true' || false;
/**
 * The DI token for the Connect interface (WS-only).
 * @internal
 */
export const WSConnect = DI.createInterface((x) => x.singleton(DefaultConnect));
/**
 * The DI token for the Connect interface (HTTP | WS).
 * @internal
 */
export const Connect = DI.createInterface((x) => useHttpConnect ? x.aliasTo(HttpConnect) : x.aliasTo(WSConnect));
/**
 * Get Connect from the DI container.
 *
 * @remarks
 * A utility method for host applications that are not using decorators or the DI container.
 *
 * @example
 * ```ts
 * import { getConnect } from '@genesislcap/foundation-comms';
 * import { API_HOST } from '@genesislcap/foundation-utils';
 * ...
 * private connect = getConnect();
 * ...
 * const connected = await this.connect.connect(API_HOST); // < WebSocket url
 * ```
 *
 * @public
 */
export function getConnect() {
    return DI.getOrCreateDOMContainer().get(Connect);
}
